import { FaPhoneAlt } from 'react-icons/fa';
import { Address, ConsignmentDetails, Customer, PointOfService } from 'types';
import { Text } from 'components';
import { BRAND_PROPERTIES, FontSize, TEST_IDS, salesMode } from 'commons';
import * as S from './styles';
import { ShippingLabelLarge, ShippingLabelSmall } from './templates';

interface Props {
  consignmentDetails: ConsignmentDetails;
  isLarge?: boolean;
}

function formatDisplayCustomer({
  shippingAddress,
  customer,
}: {
  shippingAddress: Address;
  customer: Customer;
}) {
  const {
    streetName,
    streetNumber,
    district,
    town,
    region: { isoCode },
    postalCode,
    complement,
  } = shippingAddress;

  const deliveryAddress = `
    ${streetName ? `${streetName},` : ''}
    ${streetNumber ? `${streetNumber}, ` : ''}
    ${district ? `${district}, ` : ''}
    ${town ? `${town} - ` : ''}
    ${isoCode ? `${isoCode}, ` : ''}
    ${postalCode}
  `;
  const districtAndTown = `
    ${district ? `${district}, ` : ''}
    ${town ? `${town} - ` : ''}
    ${isoCode ? isoCode : ''}
  `;
  const street = `
    ${streetName ? `${streetName}, ` : ''}
    ${streetNumber ? `${streetNumber}` : ''}
  `;
  const fullCustomerName = `${customer.firstName} ${customer.lastName}`;
  const phone = customer.phone || customer.cellphone;

  return {
    name: fullCustomerName,
    street,
    postalCode,
    districtAndTown,
    address: deliveryAddress,
    renderPhone: renderPhone(phone),
    complement,
  };
}

function formatDisplayStore(reservedPOS: PointOfService) {
  const { address, phone, displayName } = reservedPOS;
  const {
    streetName,
    streetNumber,
    district,
    town,
    region: { isoCode },
    postalCode,
    phone1,
    phone2,
  } = address;

  const street = `
    ${streetName ? `${streetName}, ` : ''}
    ${streetNumber ? `${streetNumber}` : ''}
  `;
  const fullTown = `
    ${town ? `${town} - ` : ''}
    ${isoCode ? isoCode : ''}
  `;
  const districtAndTown = `
    ${district ? `${district}, ` : ''}
    ${town ? `${town} - ` : ''}
    ${isoCode ? isoCode : ''}
  `;
  const phoneToRender = phone || phone1 || phone2;

  return {
    name: displayName,
    street,
    postalCode,
    district,
    fullTown,
    districtAndTown,
    renderPhone: renderPhone(phoneToRender),
  };
}

function renderPhone(phone: string) {
  if (!phone) return null;

  return (
    <S.IconWrapper>
      <S.Icon>
        <FaPhoneAlt size={11} />
      </S.Icon>

      <Text size={FontSize.font12}>{phone}</Text>
    </S.IconWrapper>
  );
}

export function ShippingLabel({ consignmentDetails, isLarge = false }: Props) {
  const {
    code,
    shippingAddress,
    customer,
    baseStore,
    reservedPOS,
    deliveryMode,
  } = consignmentDetails;
  const brandLogo = BRAND_PROPERTIES[baseStore].icon;
  const storeDisplay = formatDisplayStore(reservedPOS);
  const customerDisplay = formatDisplayCustomer({
    shippingAddress,
    customer,
  });

  const displayData = {
    brandLogo,
    consignmentCode: code,
    storeDisplay,
    customerDisplay,
    isPickupInStore: consignmentDetails.salesMode === salesMode.pickup,
    deliveryMode,
  };

  if (isLarge) {
    return (
      <ShippingLabelLarge
        displayData={displayData}
        data-testid={TEST_IDS.PRINT_LABEL_DISPLAY_LARGE}
      />
    );
  }

  return (
    <ShippingLabelSmall
      displayData={displayData}
      data-testid={TEST_IDS.PRINT_LABEL_DISPLAY_SMALL}
    />
  );
}
